import { useState, useEffect } from 'react';
import { useNavigate, useNavigation } from 'react-router-dom';
import styled from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import colors from '../../styles/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdCard, faBoxOpen, faTrophy, faLayerGroup, faClockRotateLeft, faCoins, faShop, faCircleQuestion, faGem, faRightLeft,
} from '@fortawesome/free-solid-svg-icons';
import { useAppSelector } from '../../store/hooks';
import { apiGetMyCardAccount } from '../../api/userCardApi';
import DailyClaimButton from '../../components/Cards/DailyClaimButton';

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 56px 24px 80px;
`;

// ─── Hero ─────────────────────────────────────────────────────────────────────

const Hero = styled.div`
  margin-bottom: 48px;
`;

const HeroEyebrow = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 215, 0, 0.55);
  margin-bottom: 10px;
`;

const HeroTitle = styled.h1`
  font-size: 40px;
  font-weight: 900;
  color: #fff;
  margin: 0 0 10px;
  line-height: 1.1;
`;

const HeroSub = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

// ─── Account section ──────────────────────────────────────────────────────────

const AccountSection = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 48px;
  flex-wrap: wrap;
`;

const CreditsCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 12px;
  padding: 16px 24px;
  flex: 1;
  min-width: 200px;
`;

const CreditsIconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background-color: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: gold;
  font-size: 18px;
  flex-shrink: 0;
`;

const CreditsTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CreditsLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const CreditsValue = styled.span`
  font-size: 26px;
  font-weight: 900;
  color: gold;
  line-height: 1;
`;

// ─── Divider ──────────────────────────────────────────────────────────────────

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.2);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.06);
  }
`;

// ─── Nav tiles ────────────────────────────────────────────────────────────────

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
`;

const Tile = styled.button<{ $accent: string; $cta?: boolean }>`
  background-color: ${({ $cta }) => ($cta ? 'rgba(34, 197, 94, 0.07)' : colors.bgColorBlack2)};
  border: 1px solid ${({ $accent }) => $accent}44;
  border-radius: 12px;
  padding: 22px 20px 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  position: relative;
  overflow: hidden;
  transition: border-color 0.18s, background-color 0.18s, transform 0.15s, box-shadow 0.18s;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ $accent }) => $accent};
    opacity: 0.6;
    transition: opacity 0.18s;
  }

  &:hover:not(:disabled) {
    border-color: ${({ $accent }) => $accent}88;
    background-color: ${({ $accent }) => $accent}0d;
    transform: translateY(-2px);
    box-shadow: 0 6px 24px ${({ $accent }) => $accent}1a;

    &::before { opacity: 1; }
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const TileIconBox = styled.div<{ $accent: string }>`
  width: 40px;
  height: 40px;
  border-radius: 9px;
  background-color: ${({ $accent }) => $accent}18;
  border: 1px solid ${({ $accent }) => $accent}33;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $accent }) => $accent};
  font-size: 16px;
  flex-shrink: 0;
`;

const TileLabel = styled.span<{ $accent: string }>`
  font-size: 15px;
  font-weight: 700;
  color: ${({ $accent }) => $accent};
  line-height: 1.2;
`;

const TileSub = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.45;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CardsLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const navigation = useNavigation();
  const isListaLoading =
    navigation.state === 'loading' &&
    navigation.location?.pathname === '/carte/lista';

  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );
  const [credits, setCredits] = useState<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiGetMyCardAccount()
      .then((data) => setCredits(data.credits))
      .catch(() => setCredits(null));
  }, [isAuthenticated]);

  return (
    <CommonWrapper>
      <PageInner>

        <Hero>
          <HeroEyebrow>Collezione ufficiale</HeroEyebrow>
          <HeroTitle>GWF Cards</HeroTitle>
          <HeroSub>Colleziona, sfoglia e scala la classifica dei migliori collezionisti GWF.</HeroSub>
        </Hero>

        {isAuthenticated && (
          <>
            <SectionLabel>Il tuo account</SectionLabel>
            <AccountSection>
              <CreditsCard>
                <CreditsIconBox>
                  <FontAwesomeIcon icon={faCoins} />
                </CreditsIconBox>
                <CreditsTextBlock>
                  <CreditsLabel>Crediti disponibili</CreditsLabel>
                  <CreditsValue>{credits !== null ? credits : '—'}</CreditsValue>
                </CreditsTextBlock>
              </CreditsCard>
              <DailyClaimButton onClaimed={(c) => setCredits(c)} />
            </AccountSection>
          </>
        )}

        <SectionLabel>Esplora</SectionLabel>
        <TilesGrid>
          <Tile $accent="#4a9eff" onClick={() => navigate('/carte/lista')} disabled={isListaLoading}>
            <TileIconBox $accent="#4a9eff">
              <FontAwesomeIcon icon={faIdCard} />
            </TileIconBox>
            <div>
              <TileLabel $accent="#4a9eff">{isListaLoading ? 'Caricamento...' : 'Lista Carte'}</TileLabel>
              <TileSub style={{ display: 'block', marginTop: 4 }}>Sfoglia il catalogo completo</TileSub>
            </div>
          </Tile>

          {isAuthenticated && (
            <Tile $accent="#22c55e" $cta onClick={() => navigate('/carte/pacchetti')}>
              <TileIconBox $accent="#22c55e">
                <FontAwesomeIcon icon={faBoxOpen} />
              </TileIconBox>
              <div>
                <TileLabel $accent="#22c55e">Acquista Pacchetti</TileLabel>
                <TileSub style={{ display: 'block', marginTop: 4 }}>Apri nuovi pacchetti carte</TileSub>
              </div>
            </Tile>
          )}

          {isAuthenticated && (
            <Tile $accent="#f97316" onClick={() => navigate('/carte/negozio')}>
              <TileIconBox $accent="#f97316">
                <FontAwesomeIcon icon={faShop} />
              </TileIconBox>
              <div>
                <TileLabel $accent="#f97316">Negozio Carte</TileLabel>
                <TileSub style={{ display: 'block', marginTop: 4 }}>Acquista singole carte</TileSub>
              </div>
            </Tile>
          )}

          <Tile $accent="gold" onClick={() => navigate('/carte/classifica')}>
            <TileIconBox $accent="gold">
              <FontAwesomeIcon icon={faTrophy} />
            </TileIconBox>
            <div>
              <TileLabel $accent="gold">Classifica</TileLabel>
              <TileSub style={{ display: 'block', marginTop: 4 }}>I migliori collezionisti GWF</TileSub>
            </div>
          </Tile>

          <Tile $accent="#a855f7" onClick={() => navigate('/carte/espansioni')}>
            <TileIconBox $accent="#a855f7">
              <FontAwesomeIcon icon={faLayerGroup} />
            </TileIconBox>
            <div>
              <TileLabel $accent="#a855f7">Espansioni</TileLabel>
              <TileSub style={{ display: 'block', marginTop: 4 }}>La tua progressione per set</TileSub>
            </div>
          </Tile>

          <Tile $accent="#38bdf8" onClick={() => navigate('/carte/quiz')}>
            <TileIconBox $accent="#38bdf8">
              <FontAwesomeIcon icon={faCircleQuestion} />
            </TileIconBox>
            <div>
              <TileLabel $accent="#38bdf8">Quiz</TileLabel>
              <TileSub style={{ display: 'block', marginTop: 4 }}>Metti alla prova le tue conoscenze</TileSub>
            </div>
          </Tile>

          <Tile $accent="#38bdf8" onClick={() => navigate('/carte/quiz/classifica')}>
            <TileIconBox $accent="#38bdf8">
              <FontAwesomeIcon icon={faTrophy} />
            </TileIconBox>
            <div>
              <TileLabel $accent="#38bdf8">Classifica Quiz</TileLabel>
              <TileSub style={{ display: 'block', marginTop: 4 }}>Chi risponde meglio alle domande</TileSub>
            </div>
          </Tile>

          {isAuthenticated && (
            <Tile $accent="#10b981" onClick={() => navigate('/carte/cedi')}>
              <TileIconBox $accent="#10b981">
                <FontAwesomeIcon icon={faRightLeft} />
              </TileIconBox>
              <div>
                <TileLabel $accent="#10b981">Cedi Carte</TileLabel>
                <TileSub style={{ display: 'block', marginTop: 4 }}>Cedi le copie in eccesso per crediti</TileSub>
              </div>
            </Tile>
          )}

          <Tile $accent="#f43f5e" onClick={() => navigate('/carte/introvabili')}>
            <TileIconBox $accent="#f43f5e">
              <FontAwesomeIcon icon={faGem} />
            </TileIconBox>
            <div>
              <TileLabel $accent="#f43f5e">Introvabili</TileLabel>
              <TileSub style={{ display: 'block', marginTop: 4 }}>Le carte meno possedute dalla community</TileSub>
            </div>
          </Tile>

          {isAuthenticated && (
            <Tile $accent="rgba(180,180,180,0.9)" onClick={() => navigate('/carte/storico')}>
              <TileIconBox $accent="rgba(180,180,180,0.9)">
                <FontAwesomeIcon icon={faClockRotateLeft} />
              </TileIconBox>
              <div>
                <TileLabel $accent="rgba(180,180,180,0.9)">Storico</TileLabel>
                <TileSub style={{ display: 'block', marginTop: 4 }}>Crediti guadagnati e spesi</TileSub>
              </div>
            </Tile>
          )}
        </TilesGrid>

      </PageInner>
    </CommonWrapper>
  );
};

export default CardsLandingPage;
